import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { fetchIssues, updateIssueInList, addIssueToList, removeIssueFromList } from '../store/slices/issueSlice'
import { Permission } from '../hooks/usePermissions'
import PermissionGate from '../components/PermissionGate'
import KanbanBoard from '../components/KanbanBoard'
import CreateStatusModal from '../components/CreateStatusModal'
import socketService from '../services/socket'
import { Issue } from '@/types'

const ProjectBoard = () => {
  const { projectKey } = useParams<{ projectKey: string }>()
  const dispatch = useAppDispatch()
  const { currentProject } = useAppSelector(state => state.project)
  const { issues, loading } = useAppSelector(state => state.issue)
  const [isCreateStatusModalOpen, setIsCreateStatusModalOpen] = useState(false)
  
  // 使用專案權限
  useEffect(() => {
    if (projectKey) {
      console.log(`Loading issues for project: ${projectKey}`)
      dispatch(fetchIssues(projectKey))
    }
  }, [dispatch, projectKey])

  // 當專案變更時，清理之前的狀態並重新連接 Socket
  useEffect(() => {
    if (currentProject?.id) {
      // 強制重新載入議題，確保顯示最新狀態
      if (projectKey && projectKey === currentProject.key) {
        console.log(`Project changed to: ${currentProject.name}, reloading issues`)
        dispatch(fetchIssues(projectKey))
      }
    }
  }, [currentProject?.id, projectKey, dispatch])

  // Socket.IO 即時更新
  useEffect(() => {
    if (currentProject?.id) {
      // 連接 Socket 並加入專案房間
      const socket = socketService.connect()
      if (socket) {
        socketService.joinProject(currentProject.id)

        // 確保 Socket 連接成功後才設置事件監聽器
        if (socket.connected) {
          console.log(`Socket connected to project: ${currentProject.id}`)
        } else {
          socket.on('connect', () => {
            console.log(`Socket connected to project: ${currentProject.id}`)
            socketService.joinProject(currentProject.id) // 重新加入房間
          })
        }
      }

      // 監聽議題建立事件
      const handleIssueCreate = (data: { issue: Issue; project: { id: string } }) => {
        console.log('Real-time issue create received:', data)
        dispatch(addIssueToList(data.issue))
      }

      // 監聽議題更新事件
      const handleIssueUpdate = (data: { issue: Issue; changes: string[] }) => {
        console.log('Real-time issue update received:', data)
        dispatch(updateIssueInList(data.issue))
      }

      // 監聽議題刪除事件
      const handleIssueDelete = (data: { issueId: string; projectId: string }) => {
        console.log('Real-time issue delete received:', data)
        dispatch(removeIssueFromList(data.issueId))
      }

      // 監聽議題移動事件（從當前專案移出）
      const handleIssueMoved = (data: { issueId: string; fromProject: string; toProject: string; userId: string }) => {
        console.log('Real-time issue move received:', data)
        dispatch(removeIssueFromList(data.issueId))
      }

      // 監聽議題接收事件（移入當前專案）
      const handleIssueReceived = (data: { issue: Issue; fromProject: string; userId: string }) => {
        console.log('Real-time issue received:', data)
        // 確認議題是移動到當前專案
        if (data.issue.projectId === currentProject.id) {
          dispatch(addIssueToList(data.issue))
        } else {
          // 如果專案 ID 不匹配，可能是同步問題，重新載入議題
          console.warn('Issue project mismatch, reloading issues')
          if (projectKey) {
            dispatch(fetchIssues(projectKey))
          }
        }
      }

      socketService.onIssueCreated(handleIssueCreate)
      socketService.onIssueUpdated(handleIssueUpdate)
      socketService.onIssueDeleted(handleIssueDelete)
      socketService.onIssueMoved(handleIssueMoved)
      socketService.onIssueReceived(handleIssueReceived)

      // 清理函數
      return () => {
        socketService.offIssueCreated()
        socketService.offIssueUpdated()
        socketService.offIssueDeleted()
        socketService.offIssueMoved()
        socketService.offIssueReceived()
        if (currentProject?.id) {
          socketService.leaveProject(currentProject.id)
        }
      }
    }
  }, [currentProject?.id, dispatch])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900">專案不存在</h3>
        <p className="text-gray-500">請檢查專案代碼是否正確</p>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="space-y-6">
        {/* 看板 */}
        <KanbanBoard 
          issues={issues} 
          projectId={currentProject?.id!} 
          onCreateStatus={() => setIsCreateStatusModalOpen(true)}
        />

        {/* 建立狀態模態框 */}
        <PermissionGate 
          permission={Permission.MANAGE_BOARD_STATUS} 
          projectId={currentProject?.id}
        >
          <CreateStatusModal
            isOpen={isCreateStatusModalOpen}
            onClose={() => setIsCreateStatusModalOpen(false)}
            projectId={currentProject?.id!}
          />
        </PermissionGate>
      </div>
    </div>
  )
}

export default ProjectBoard
